/**
 * Respuesta de la verificación de bloqueo de programación.
 */
export interface VerificacionBloqueoResponse {
  /** True si la orden está bloqueada para el usuario actual */
  bloqueada: boolean;
  /** Consecutivo de la orden verificada */
  nmconsecutivo: number;
  /** Consecutivo del usuario que verificó */
  cdUsuario: number;
}

/**
 * Request para bloquear una orden de servicio.
 */
export interface BloquearOrdenRequest {
  /** Consecutivo del detalle de orden a bloquear (obligatorio) */
  nmconsecutivoTarea: number;
  /** Tipo de identificación del usuario (ej: "CC") (obligatorio) */
  cdTipoIdUsuario: string;
  /** Número de identificación del usuario (obligatorio) */
  nmIdUsuario: string;
  /** Consecutivo del empleado en sesión (obligatorio) */
  cdUsuario: number;
  // Campos opcionales (filtros)
  cdTipoIdCliente?: string;
  nmIdCliente?: string;
  cdCliente?: string;
  cdTipoIdBenef?: string;
  nmIdBenef?: string;
  cdBenef?: number;
  cdTipoIdAsesor?: string;
  nmIdAsesor?: string;
  cdAsesor?: number;
  feAprobacionIni?: string;
  feAprobacionFin?: string;
  snProgramado?: string;
  snPrioridad?: string;
  cdTipoMicroSeg?: number;
  cdTipoMacroSeg?: number;
  cdTipoAtencion?: number;
  nmConsUnidadAdmin?: number;
  snTareaProgramable?: string;
  snSoloAsesorProgramable?: string;
  snTareaAutomatica?: string;
  nmConsecutivoCiaConsult?: number;
  cdUnidadAdmin?: string;
  consecutivoManualEncab?: string;
}

/**
 * Request para desbloquear órdenes de servicio.
 */
export interface DesbloquearOrdenesRequest {
  /** Consecutivo del empleado en sesión */
  nmconsecutivoUsuario: number;
  /** Clave del módulo para desbloquear (default: "SCHEDULE") */
  opcionBloqueo?: string;
}

/**
 * Response de operaciones de bloqueo/desbloqueo.
 */
export interface BloqueoResponse {
  /** True si la operación fue exitosa */
  exitoso: boolean;
  /** Mensaje descriptivo para mostrar al usuario */
  mensajeUsuario?: string;
  /** Mensaje técnico para debugging */
  mensajeTecnico?: string;
}

/**
 * Puerto Abstracto: BloqueoProgramacionRepository
 *
 * Define el contrato para operaciones de bloqueo de programación.
 * Este módulo es genérico e independiente de órdenes de servicio.
 *
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpBloqueoProgramacionRepository (adaptador) lo implementa en infraestructura
 *
 * Operaciones disponibles:
 * - verificarBloqueo: Verifica si una tarea está bloqueada para el usuario
 * - bloquearOrden: Bloquea una orden para el usuario actual
 * - desbloquearOrdenes: Desbloquea todas las órdenes del usuario para un módulo
 *
 * @author GIROP - Migración GWT a Angular
 */
export abstract class BloqueoProgramacionRepository {

  /**
   * Verifica si una orden/tarea está bloqueada para el usuario actual.
   *
   * Endpoint: GET /api/servicios/bloqueo-programacion/verificar
   *
   * @param nmconsecutivo Consecutivo del detalle de orden
   * @param cdUsuario     Consecutivo del empleado en sesión
   * @returns True si está bloqueada, false si disponible
   *
   * @legacy FN_SNTAREA_BLOQ_PARA_USR
   */
  abstract verificarBloqueo(
    nmconsecutivo: number,
    cdUsuario: number
  ): Promise<boolean>;

  /**
   * Bloquea una orden/tarea para el usuario actual.
   *
   * Endpoint: POST /api/servicios/bloqueo-programacion/bloquear
   *
   * Se invoca cuando el usuario abre el diálogo de "Programar servicio" o "Detallar".
   * Marca la orden como bloqueada para evitar que otros usuarios la programen.
   *
   * @param request Datos para el bloqueo (nmconsecutivoTarea + usuario obligatorios)
   * @returns Resultado con exitoso y mensajes del SP
   *
   * @legacy ProgramarServiciosActivity.bloquearRegistros()
   */
  abstract bloquearOrden(request: BloquearOrdenRequest): Promise<BloqueoResponse>;

  /**
   * Desbloquea todas las órdenes bloqueadas por el usuario para un módulo.
   *
   * Endpoint: POST /api/servicios/bloqueo-programacion/desbloquear
   *
   * Se invoca cuando el usuario cierra el diálogo de programación o navega fuera.
   * Libera todas las órdenes para que otros usuarios puedan programarlas.
   *
   * @param request Datos para el desbloqueo (nmconsecutivoUsuario + opcionBloqueo)
   * @returns Resultado con exitoso y mensajes del SP
   *
   * @legacy UtilitiesOnServer.unBlockRecordsSessionUsrOption()
   */
  abstract desbloquearOrdenes(request: DesbloquearOrdenesRequest): Promise<BloqueoResponse>;
}
